import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { Router, RouterLink, RouterLinkActive, IsActiveMatchOptions } from '@angular/router';
import { AuthenticationService } from '../../core/authentication/authentication.service';
import { Theme, ThemeService } from '../../core/services/theme.service';
import { AppUrls } from '../../routing/app-urls';
import { environment } from '../../../environments/environment';
import { ApplicationStore, ApplicationStoreType } from '../../core/store/application.store';
import { Option } from '../../shared/types/option';
import { OptionToggleComponent } from '../../shared/components/option-toggle/option-toggle.component';
import { ConversationHistoryComponent } from '../conversation-history/conversation-history.component';

@Component({
    selector: 'juyo-sidebar',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterLink, RouterLinkActive, NgOptimizedImage, OptionToggleComponent, ConversationHistoryComponent],
    styleUrl: 'sidebar.scss',
    host: { '[class.collapsed]': 'collapsed()' },
    template: `
        <div class="branding">
            @if (!collapsed()) {
                <img
                    [ngSrc]="
                        themeService.theme() === Theme.Dark
                            ? 'assets/logos/logo_white.png'
                            : 'assets/logos/logo_dark.png'
                    "
                    width="415"
                    height="129"
                    alt="Juyo"
                    priority
                />
            } @else {
                <img
                    [ngSrc]="
                        themeService.theme() === Theme.Dark
                            ? 'assets/logos/logo_icon_white.png'
                            : 'assets/logos/logo_icon_dark.png'
                    "
                    width="129"
                    height="129"
                    alt="Juyo"
                    priority
                />
            }
        </div>

        @if (!collapsed()) {
            @if (chainOptions().length > 1) {
                <juyo-option-toggle
                    [options]="chainOptions()"
                    [selectedOption]="selectedChainOption()"
                    [openOnClick]="true"
                    (optionClick)="onChainSelected($event)"
                />
            } @else if (chainOptions().length === 1) {
                <span class="chain-name">{{ chainOptions()[0].label }}</span>
            }
        }

        <nav class="nav-links">
            <a
                [routerLink]="['/', AppUrls.hotelGptUrl]"
                routerLinkActive="active"
                [routerLinkActiveOptions]="activeOptions"
            >
                <span class="material-symbols-outlined"> chat_bubble </span>
                @if (!collapsed()) {
                    HotelGPT
                }
            </a>
            <a
                [routerLink]="['/', AppUrls.dataDropUrl]"
                routerLinkActive="active"
                [routerLinkActiveOptions]="activeOptions"
            >
                <span class="material-symbols-outlined"> download </span>
                @if (!collapsed()) {
                    Data Drop
                }
            </a>
        </nav>

        <juyo-conversation-history [collapsed]="collapsed()" />

        <div class="actions">
            <button type="button" (click)="themeService.toggle()">
                <span class="theme-toggle" [class.dark]="themeService.theme() === Theme.Dark">
                    <span class="theme-toggle__icon">
                        @if (themeService.theme() === Theme.Dark) {
                            <span class="material-symbols-outlined"> light_mode </span>
                        } @else {
                            <span class="material-symbols-outlined"> dark_mode </span>
                        }
                    </span>
                </span>
                @if (!collapsed()) {
                    <span class="label">{{ themeService.theme() === Theme.Dark ? 'Dark mode' : 'Light mode' }}</span>
                }
            </button>
            <button type="button" (click)="onNavigateToAnalytics()">
                <span class="material-symbols-outlined"> open_in_new </span>
                @if (!collapsed()) {
                    <span class="label">Back to menu</span>
                }
            </button>
            <button type="button" (click)="onSendFeedback()">
                <span class="material-symbols-outlined"> forward_to_inbox </span>
                @if (!collapsed()) {
                    <span class="label">Send feedback</span>
                }
            </button>
            <button type="button" (click)="onSignOut()">
                <span class="material-symbols-outlined"> logout </span>
                @if (!collapsed()) {
                    <span class="label">Sign Out</span>
                }
            </button>
        </div>

        <button type="button" class="toggle" (click)="onToggle()">
            @if (collapsed()) {
                <span class="material-symbols-outlined icon-small"> chevron_right </span>
            } @else {
                <span class="material-symbols-outlined icon-small"> chevron_left </span>
            }
        </button>
    `,
})
export class SidebarComponent {
    private authService = inject(AuthenticationService);
    private router = inject(Router);
    private applicationStore: ApplicationStoreType = inject(ApplicationStore);
    protected themeService = inject(ThemeService);

    public collapsed = input.required<boolean>();
    public toggled = output<void>();

    protected chainOptions = computed<Option[]>(() => {
        const chains = this.applicationStore.chains();
        if (chains && chains.length > 0) {
            return chains.map((chain) => new Option(chain.id.toString(), chain.displayName));
        } else {
            return [];
        }
    });

    protected selectedChainOption = computed<Option | undefined>(() => {
        const chain = this.applicationStore.selectedChain();
        return chain ? new Option(chain.id.toString(), chain.displayName) : undefined;
    });

    protected readonly externalPlatformUrl = environment.externalPlatformUrl;
    protected readonly AppUrls = AppUrls;
    protected readonly Theme = Theme;

    protected readonly activeOptions: IsActiveMatchOptions = {
        paths: 'exact',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored',
    };

    protected onToggle(): void {
        this.toggled.emit();
    }

    protected onChainSelected(option: Option): void {
        const chain = this.applicationStore.chains().find((c) => c.id.toString() === option.key);
        if (chain) {
            this.applicationStore.setSelectedChain(chain);
        }
    }

    protected onNavigateToAnalytics(): void {
        const tokens = this.authService.getTokensForExport();
        const encoded = btoa(JSON.stringify(tokens));
        const selectedChain = this.applicationStore.selectedChain();
        const chainParam = selectedChain ? `?chainId=${selectedChain.id}` : '';
        window.location.href = `${this.externalPlatformUrl}${chainParam}#tokens=${encoded}`;
    }

    protected onSendFeedback(): void {
        window.location.href = 'mailto:juyo-ai@juyoanalytics.com';
    }

    protected async onSignOut(): Promise<void> {
        await this.authService.signOut();
        this.router.navigate([AppUrls.signInUrl]);
    }
}
